<template>
  <div
    class="card-wrapper"
    @click="() => onSendAnalytics('clickStats', 'stops_card_clicked')"
  >
    <div
      v-if="getSeasonalAsset('stopsDescriptionCardBg')"
      class="background-wrapper"
    >
      <component
        :is="getSeasonalAsset('stopsDescriptionCardBg')"
        :style="getSeasonalStyle('stopsDescriptionCardBg')"
        class="seasonal-background"
      />
    </div>
    <BmCard>
      <div class="card-content">
        <template v-if="trackingDataStore.isJobInProgress">
          <h1 class="title" v-html="title"></h1>
          <div
            v-if="
              !props.postcode ||
              !configGetter($rttI18n.tenantId, themeStore.themeId)
                .disableStopsComponent
            "
            class="in-progress-caption text-icon neutral label"
          >
            {{ caption }}
          </div>
          <div
            v-if="props.postcode && trackingDataStore.showPositionExplanation"
            class="position-info"
          >
            {{ positionInfo }}
          </div>
        </template>
        <template v-else-if="trackingDataStore.isJobFinished">
          <h1 class="job-finished-title">
            <span class="subtext">
              {{ jobFinishedCaption }}
            </span>
            <span class="heading">
              {{ jobFinishedHeading }}
            </span>
          </h1>
        </template>
        <template v-if="showDriverPositionInfoForGlsIt">
          <div id="driver-position-info" class="tracking-id">
            {{ $rttI18n.tenantSpecificContent('driverPositionInfo') }}
          </div>
        </template>
        <hr aria-hidden="true" />
        <template v-if="showTrackingNoteForGlsEs">
          <div
            id="more-info-link"
            class="semi tracking-note"
            v-html="trackingNote"
          />
          <hr aria-hidden="true" />
        </template>
        <div class="tracking-id">
          {{ $t('stopsCard.trackingId') }}: {{ trackingId }}
        </div>
        <BmButton
          v-if="showCustomizeButton"
          :on-click="
            () =>
              dialogStore.toggleDialog('CUSTOMIZE_DRAWER') &&
              onSendAnalytics(
                'customization',
                'stops_card_adhoc_button_clicked',
                {
                  remainingStops: trackingDataStore.data.status?.remainingStops
                }
              )
          "
          class="customize-button"
          size="large"
          type="primary"
          >{{ $t('customization.changeDelivery.header') }}
        </BmButton>
      </div>
    </BmCard>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue';
import { sendAnalytics } from '@/services/analytics.js';

import BmCard from '@/v2/design-system/components/Card/BmCard.vue';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { configGetter } from '@/config/configGetter.js';
import { useThemeStore } from '@/stores/theme.js';
import DOMPurify from 'dompurify';
import { getStopsStep } from '@/util/get.js';
import BmButton from '@/v2/design-system/components/Button/BmButton.vue';
import { useResponsiveBreakpointsStore } from '@/stores/responsiveBreakpoints.js';
import { useDialogStore } from '@/stores/dialog.js';
import { useSeasonalTheme } from '@/v2/composables/useSeasonalTheme.js';
import { useShowCustomize } from '@/v2/composables/useShowCustomize.js';

const props = defineProps({
  postcode: { type: String, required: false }
});

const $t = inject('t');
const $rttI18n = inject('rttI18n');

const trackingDataStore = useTrackingDataStore();
const themeStore = useThemeStore();
const responsiveBreakpointsStore = useResponsiveBreakpointsStore();
const dialogStore = useDialogStore();
const { isEligibleForCustomize } = useShowCustomize();

const { getSeasonalStyle, getSeasonalAsset } = useSeasonalTheme();

const showCustomizeButton = computed(() => {
  return (
    responsiveBreakpointsStore.isBelowMiddleBreakpoint &&
    props.postcode &&
    isEligibleForCustomize.value
  );
});

const trackingId = computed(() => {
  return trackingDataStore.data.trackingId;
});

const remainingStops = computed(() => {
  return trackingDataStore.data.status.remainingStops;
});

const displayStops = computed(() => {
  return trackingDataStore.isJobStatusShadowed
    ? getStopsStep(remainingStops.value)
    : remainingStops.value;
});

const title = computed(() => {
  if (
    trackingDataStore.isJobStatusBlocked ||
    trackingDataStore.isJobStatusUnknown ||
    trackingDataStore.isJobStatusUnclear
  ) {
    return trackingDataStore.isServiceTypeInbound
      ? `📦&nbsp;&nbsp;${$t('stopsCard.title.pickupBlocked')}`
      : `📦&nbsp;&nbsp;${$t('stopsCard.title.blocked')}`;
  }

  if (!props.postcode) {
    return trackingDataStore.isServiceTypeInbound
      ? `📦&nbsp;&nbsp;${$t('stopsCard.title.pickupNoPostCode')}`
      : `📦&nbsp;&nbsp;${$t('stopsCard.title.noPostCode')}`;
  }

  if (
    trackingDataStore.isJobStatusUnknown ||
    trackingDataStore.isJobStatusUnclear
  ) {
    return `🚫&nbsp;&nbsp;${$t('stopsCard.title.noDataAvailable')}`;
  }

  if (trackingDataStore.isJobDeliveryStatusPreEta) {
    return `📪&nbsp;&nbsp;${$t('stopsCard.title.betweenTenAndThirty')}`;
  }

  if (displayStops.value > 50) {
    return `📤&nbsp;&nbsp;${$t('stopsCard.title.moreThanFifty')}`;
  }

  if (displayStops.value > 30 && displayStops.value <= 50) {
    return `🚚&nbsp;&nbsp;${$t('stopsCard.title.betweenThirtyAndFifty')}`;
  }

  if (displayStops.value > 10 && displayStops.value <= 30) {
    return `📪&nbsp;&nbsp;${$t('stopsCard.title.betweenTenAndThirty')}`;
  }

  if (displayStops.value > 6 && displayStops.value <= 10) {
    return `🧭&nbsp;&nbsp;${$t('stopsCard.title.betweenSixAndTen')}`;
  }

  if (displayStops.value > 1 && displayStops.value <= 6) {
    return `📍&nbsp;&nbsp;${$t('stopsCard.title.betweenOneAndSix')}`;
  }

  if (displayStops.value === 0 || displayStops.value === 1) {
    return `🎉&nbsp;&nbsp;${$t('stopsCard.title.last')}`;
  }

  return '';
});

const caption = computed(() => {
  if (
    trackingDataStore.isJobStatusBlocked ||
    trackingDataStore.isJobStatusUnknown ||
    trackingDataStore.isJobStatusUnclear
  ) {
    return trackingDataStore.isServiceTypeInbound
      ? $t('stopsCard.caption.pickupBlocked')
      : $t('stopsCard.caption.blocked');
  }

  if (!props.postcode) {
    return trackingDataStore.isServiceTypeInbound
      ? $t('stopsCard.caption.pickupNoPostCode')
      : $t('stopsCard.caption.noPostCode');
  }

  if (trackingDataStore.isJobDeliveryStatusPreEta) {
    return $t('stopsCard.caption.preEta');
  }

  if (
    displayStops.value === null ||
    trackingDataStore.isJobStatusUnknown ||
    trackingDataStore.isJobStatusBlocked ||
    trackingDataStore.isJobStatusUnclear
  ) {
    return $t('stopsCard.caption.unknownOrBlocked');
  }

  if (trackingDataStore.isJobStatusShadowed) {
    return $t('stopsCard.caption.lessThanStops', {
      stops: displayStops.value
    });
  }

  if (displayStops.value > 50) {
    return $t('stopsCard.caption.moreThanFifty');
  }

  if (displayStops.value === 1) {
    return $t('stopsCard.caption.last');
  }

  if (displayStops.value === 0) {
    return $t('stopsCard.caption.zeroStops');
  }

  return (
    $rttI18n.tcOrEmpty(
      `stopsCard.caption.outForDelivery${$rttI18n.getSlavicPluralDeclinationKeySuffix(displayStops.value)}`,
      {
        stops: displayStops.value
      }
    ) ||
    $t('stopsCard.caption.outForDelivery', {
      stops: displayStops.value
    })
  );
});

const positionInfo = computed(() => {
  return $t(
    trackingDataStore.isServiceTypeInbound
      ? 'stopsCard.pickupPositionInfo'
      : 'stopsCard.positionInfo',
    {
      stops: trackingDataStore.data.maxStopsToShowDriverLocation
    }
  );
});

const jobFinishedCaption = computed(() => {
  if (
    trackingDataStore.isJobStatusDelivered ||
    trackingDataStore.isJobStatusInboundSuccess
  ) {
    return $t('trackingFinished.successSubtext');
  }

  if (
    trackingDataStore.isJobStatusNotDelivered ||
    trackingDataStore.isJobStatusInboundFail
  ) {
    return $t('trackingFinished.parcelSubtext');
  }

  return '';
});

const jobFinishedHeading = computed(() => {
  if (trackingDataStore.isJobStatusInboundSuccess) {
    return $t('trackingFinished.pickedUp.heading');
  }

  if (trackingDataStore.isJobStatusInboundFail) {
    return $t('trackingFinished.notPickedUp.heading');
  }

  if (trackingDataStore.isJobStatusDelivered) {
    return $t('trackingFinished.delivered.heading');
  }

  if (trackingDataStore.isJobStatusNotDelivered) {
    return $t('trackingFinished.notDelivered.heading');
  }

  return '';
});

const showTrackingNoteForGlsEs = computed(() => {
  return (
    $rttI18n.tenantId &&
    $rttI18n.isGlsEsSelected &&
    !configGetter($rttI18n.tenantId, themeStore.themeId).disableMoreInfoLink &&
    props.postcode
  );
});

const showDriverPositionInfoForGlsIt = computed(() => {
  return (
    $rttI18n.tenantId &&
    $rttI18n.isGlsItSelected &&
    props.postcode &&
    displayStops.value <= trackingDataStore.data.maxStopsToShowDriverLocation
  );
});

const trackingNote = computed(() => {
  const note = $rttI18n.tenantSpecificContent('glsTrackingNote', {
    trackingId: trackingId.value,
    postcode: props.postcode
  });
  const noteWithLinkId = note.replace('<a ', '<a id="more-info-link" ');

  return DOMPurify.sanitize(noteWithLinkId, {
    ADD_ATTR: ['target', 'rel']
  });
});

const onSendAnalytics = (category, name, payload = {}) => {
  sendAnalytics(trackingDataStore.data, category, name, {
    ...payload,
    isV2: true
  });
};
</script>

<style scoped>
.customize-button {
  width: 100%;
}

.title {
  color: var(--neutral-text-icon-pressed);
  font-family: var(--body-base-semi-family), 'sans-serif';
  font-size: var(--body-base-semi-size);
  font-style: normal;
  font-weight: var(--body-base-semi-weight);
  line-height: var(--line-height-lead-md);
  text-align: center;
}

.job-finished-title {
  text-align: center;

  .heading {
    color: var(--neutral-text-icon-title);
    font-family: var(--font-family-header-bold), sans-serif;
    font-size: var(--header-h-2-bold-size);
    font-style: normal;
    font-weight: var(--font-weight-medium);
    line-height: var(--header-h-2-bold-line-height);
    display: block;
  }

  .subtext {
    color: var(--neutral-text-icon-label);
    font-family: var(--body-small-med-family), 'sans-serif';
    font-size: var(--body-small-med-size);
    font-style: normal;
    font-weight: var(--body-small-med-weight);
    line-height: var(--line-height-lead-sm);
    margin-bottom: 0.5em;
    display: block;
  }
}

.card-wrapper {
  font-size: var(--body-small-semi-size);

  .card {
    padding: 108px var(--pad-space-sp-24) var(--pad-space-sp-24)
      var(--pad-space-sp-24);
  }
}

@media (width < 480px) {
  .card-wrapper {
    width: 100%;
  }
}

.card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pad-space-sp-14);
}

.background-wrapper {
  position: relative;
  pointer-events: none;
}

.seasonal-background {
  position: absolute;
  left: 1px;
  right: 1px;
  pointer-events: none;
}

hr {
  border: 1px solid var(--neutral-border-subtle);
  width: 100%;
}

.in-progress-caption {
  text-align: center;
  color: var(--neutral-text-icon-label);
  font-size: var(--body-small-med-size);
  font-weight: var(--body-small-med-weight);
  line-height: var(--line-height-lead-sm);
}

.position-info,
.tracking-id {
  text-align: center;
  color: var(--neutral-text-icon-label);
  font-size: var(--label-caption-med-size);
  font-weight: var(--label-caption-med-weight);
  line-height: var(--label-caption-med-line-height);
}

.tracking-note {
  text-align: center;

  &:deep(a) {
    text-decoration: none;
    color: var(--neutral-text-icon-pressed);
  }

  &:deep(a:visited) {
    color: var(--neutral-text-icon-pressed);
  }

  &:deep(a .click-cta) {
    text-decoration: underline;
    color: var(--primary-text-icon-default);
  }
}
</style>
